import { Settings } from '@streamr/tv-engine/~/settings';
import { proxy, transfer, wrap } from 'comlink';
import { EventEmitter } from 'eventemitter3';
import { createWorker } from '../../utils/workerUtils';
import { AudioFrameExtractor } from './AudioFrameExtractor';
import { createAudioDecoder } from './createAudioDecoder';
import { createAudioFrameExtractor } from './createAudioFrameExtractor';
import { isGapDiagnosticsEnabled, logGapDiagnostic } from './GapDiagnostics';
import { IncomingTrack } from './IncomingTrack';
import { AudioPlayer } from './multiaudioplayer/AudioPlayer';
import { Timekeeper } from './Timekeeper';
/**
 * Wraps an extractor output track, applying a gain multiplier to every
 * audio frame's Float32Array samples before forwarding to the encoder.
 * Preserves timestamps exactly so the encoder FIFO stays in sync.
 */
class GainApplyingTrack {
    source;
    getGain;
    constructor(source, getGain) {
        this.source = source;
        this.getGain = getGain;
    }
    getTrackId() {
        return this.source.getTrackId();
    }
    isLocal() {
        return this.source.isLocal();
    }
    setListener(listener) {
        this.source.setListener(async (frame, done) => {
            if (frame) {
                const gain = this.getGain();
                if (gain !== 1) {
                    const scaled = new Float32Array(frame.data.length);
                    for (let i = 0; i < frame.data.length; i++) {
                        scaled[i] = frame.data[i] * gain;
                    }
                    await listener({ ...frame, data: scaled }, done);
                    return;
                }
            }
            await listener(frame, done);
        });
    }
    unsetListener() {
        this.source.unsetListener();
    }
    hasListener() {
        return this.source.hasListener();
    }
    getBufferLength() {
        return this.source.getBufferLength();
    }
}
export class AudioEngine {
    LOG_PREFIX = '[AudioEngine]';
    audioFrameExtractors = new Map();
    audioDecoders = new Map();
    pendingTrackIds = new Set();
    audioContext;
    audioPlayer;
    incomingTracks = new Map();
    encodedAudioOutputTrackAddedCallback;
    // Composite audio capture state (extractors live on main thread)
    compositeAudioExtractor;
    // Original input tracks so they can be mixed into the composite capture
    inputAudioTracks = new Map();
    // Per-input-track gain multiplier (0.0–1.0)
    inputTrackGains = new Map();
    perTrackEncodingEnabled = false;
    // On-demand audio encoder worker
    audioEncoderWorkerRaw;
    audioEncoderWorkerProxy;
    // On-demand audio decoder worker
    audioDecoderWorkerRaw;
    audioDecoderWorkerProxy;
    referenceTimeEmitter = new EventEmitter();
    constructor(audioContext) {
        this.audioContext = audioContext;
    }
    setPerTrackEncodingEnabled(enabled) {
        this.perTrackEncodingEnabled = enabled;
    }
    async init(getOptimalDelayCallback, encodedAudioOutputTrackAddedCallback) {
        this.encodedAudioOutputTrackAddedCallback =
            encodedAudioOutputTrackAddedCallback;
        this.audioPlayer = new AudioPlayer(this.audioContext, getOptimalDelayCallback);
        this.audioPlayer.on('newReferenceTimes', (trackId, presentationTimeMicros, realTimeMillis) => {
            this.referenceTimeEmitter.emit('newReferenceTimes', trackId, presentationTimeMicros, realTimeMillis);
        });
        this.audioPlayer.on('playbackStarted', (trackId) => {
            this.referenceTimeEmitter.emit('playbackStarted', trackId);
        });
        this.audioPlayer.on('audioUnderflow', (trackId) => {
            this.reportAudioUnderflow(trackId).catch(console.error);
            this.referenceTimeEmitter.emit('audioUnderflow', trackId);
        });
        await this.audioPlayer.init();
    }
    async destroy() {
        console.log(`${this.LOG_PREFIX} destroy`);
        await this.stopAllN1Encoders();
        for (const [trackId, extractor] of this.audioFrameExtractors) {
            console.log(`${this.LOG_PREFIX} stopping extractor`, {
                trackId: trackId.slice(0, 8),
            });
            await extractor.stop();
        }
        this.audioFrameExtractors.clear();
        if (this.audioEncoderWorkerProxy) {
            await this.stopAudioEncoderWorker();
        }
        if (this.audioDecoderWorkerProxy) {
            await this.stopAudioDecoderWorker();
        }
        for (const [trackId, decoder] of this.audioDecoders) {
            console.log(`${this.LOG_PREFIX} destroying decoder`, {
                trackId: trackId.slice(0, 8),
            });
            await decoder.destroy();
        }
        this.audioDecoders.clear();
        this.incomingTracks.clear();
        this.pendingTrackIds.clear();
        this.inputAudioTracks.clear();
        this.inputTrackGains.clear();
        await this.audioPlayer?.dispose();
        this.audioPlayer = undefined;
        await AudioFrameExtractor.teardown();
        console.log(`${this.LOG_PREFIX} destroy complete`);
    }
    // ========== Audio encoder worker lifecycle ==========
    async startAudioEncoderWorker() {
        if (this.audioEncoderWorkerProxy) {
            console.warn(`${this.LOG_PREFIX} audioEncoderWorker already running`);
            return;
        }
        this.audioEncoderWorkerRaw = this.createAudioEncoderWorker();
        this.audioEncoderWorkerRaw.addEventListener('message', (e) => {
            if (e.data?.type === 'compositeBucketStats') {
                logGapDiagnostic('audioEncoder.compositeBucket', {
                    detail: {
                        role: e.data.role,
                        frameCount: e.data.frameCount,
                        totalDurationUs: e.data.totalDurationUs,
                        durationMs: e.data.durationMs,
                    },
                });
            }
        });
        this.audioEncoderWorkerProxy = wrap(this.audioEncoderWorkerRaw);
        await this.audioEncoderWorkerProxy.init(proxy(async (encodedTrack) => {
            this.encodedAudioOutputTrackAddedCallback?.(encodedTrack);
        }));
        console.log(`${this.LOG_PREFIX} audioEncoderWorker started`);
    }
    async stopAudioEncoderWorker() {
        try {
            await this.audioEncoderWorkerProxy?.destroy();
        }
        catch (error) {
            console.warn(`${this.LOG_PREFIX} audioEncoderWorker destroy failed`, error);
        }
        this.audioEncoderWorkerRaw?.terminate();
        this.audioEncoderWorkerProxy = undefined;
        this.audioEncoderWorkerRaw = undefined;
        console.log(`${this.LOG_PREFIX} audioEncoderWorker stopped`);
    }
    isAudioEncoderWorkerRunning() {
        return this.audioEncoderWorkerProxy !== undefined;
    }
    async connectEncoderNetworkingPort(port) {
        if (!this.audioEncoderWorkerProxy) {
            throw new Error('AudioEncoderWorker not running');
        }
        await this.audioEncoderWorkerProxy.connectNetworkingPort(transfer(port, [port]));
    }
    async connectCompositeNetworkingPort(port) {
        if (!this.audioEncoderWorkerProxy) {
            throw new Error('AudioEncoderWorker not running');
        }
        await this.audioEncoderWorkerProxy.connectCompositeNetworkingPort(transfer(port, [port]));
    }
    async connectDecoderNetworkingPort(port) {
        if (!this.audioDecoderWorkerProxy) {
            throw new Error('AudioDecoderWorker not running');
        }
        await this.audioDecoderWorkerProxy.connectNetworkingPort(transfer(port, [port]));
    }
    getAudioEncoderWorkerRaw() {
        return this.audioEncoderWorkerRaw;
    }
    // ========== Audio decoder worker lifecycle ==========
    async startAudioDecoderWorker() {
        if (this.audioDecoderWorkerProxy) {
            console.warn(`${this.LOG_PREFIX} audioDecoderWorker already running`);
            return;
        }
        this.audioDecoderWorkerRaw = this.createAudioDecoderWorker();
        this.audioDecoderWorkerRaw.addEventListener('message', (e) => {
            if (e.data?.type === 'gapDiagnostic') {
                logGapDiagnostic(e.data.layer, {
                    trackId: e.data.trackId,
                    deltaMs: e.data.deltaMs,
                    detail: {
                        smoothedDelayMs: e.data.smoothedDelayMs,
                        rawDelayMs: e.data.rawDelayMs,
                    },
                });
            }
        });
        this.audioDecoderWorkerProxy = wrap(this.audioDecoderWorkerRaw);
        await this.audioDecoderWorkerProxy.init(proxy((trackId, _codec) => {
            this.handleNewDecoderTrack(trackId);
        }), proxy((trackId) => {
            this.handleDecoderTrackDone(trackId);
        }), proxy((trackId, optimalDelay) => {
            this.referenceTimeEmitter.emit('audioOptimalDelay', trackId, optimalDelay);
        }), isGapDiagnosticsEnabled());
        console.log(`${this.LOG_PREFIX} audioDecoderWorker started`);
    }
    async reportAudioUnderflow(trackId) {
        await this.audioDecoderWorkerProxy?.reportUnderflow(trackId);
    }
    async stopAudioDecoderWorker() {
        try {
            await this.audioDecoderWorkerProxy?.destroy();
        }
        catch (error) {
            console.warn(`${this.LOG_PREFIX} audioDecoderWorker destroy failed`, error);
        }
        this.audioDecoderWorkerRaw?.terminate();
        this.audioDecoderWorkerProxy = undefined;
        this.audioDecoderWorkerRaw = undefined;
        console.log(`${this.LOG_PREFIX} audioDecoderWorker stopped`);
    }
    isAudioDecoderWorkerRunning() {
        return this.audioDecoderWorkerProxy !== undefined;
    }
    getAudioDecoderWorkerRaw() {
        return this.audioDecoderWorkerRaw;
    }
    /**
     * Called by AudioDecoderWorker when a new track is discovered.
     * Creates an AudioWorkletNode + MessageChannel and wires the decoder port.
     */
    handleNewDecoderTrack(trackId) {
        if (!this.audioPlayer)
            return;
        const { decoderPort } = this.audioPlayer.createTrackForDecoderPort(trackId);
        this.audioDecoderWorkerProxy?.connectTrackPort(trackId, transfer(decoderPort, [decoderPort]));
    }
    /**
     * Called by AudioDecoderWorker when a track is done.
     */
    handleDecoderTrackDone(trackId) {
        logGapDiagnostic('audioEngine.decoderTrackDone', {
            trackId,
            detail: {
                activePlayerTracks: this.audioPlayer?.getActiveTrackIds() ?? [],
            },
        });
        this.audioPlayer?.removeTrack(trackId);
    }
    /**
     * Update optimal delay for a decoder-managed track's worklet.
     */
    updateDecoderTrackOptimalDelay(trackId) {
        this.audioPlayer?.updateTrackOptimalDelay(trackId);
    }
    // ========== Per-track audio (extractor on main thread, encoder in worker) ==========
    async addMediaStreamAudioTrack(track) {
        const trackId = track.id;
        console.log(`${this.LOG_PREFIX} addMediaStreamAudioTrack`, {
            trackId: trackId.slice(0, 8),
            hasEncoderWorker: !!this.audioEncoderWorkerProxy,
        });
        if (this.audioFrameExtractors.has(trackId) ||
            this.pendingTrackIds.has(trackId)) {
            console.log(`${this.LOG_PREFIX} Skipping duplicate audio track`, {
                trackId: trackId.slice(0, 8),
            });
            return;
        }
        this.pendingTrackIds.add(trackId);
        let audioFrameExtractor;
        try {
            audioFrameExtractor = createAudioFrameExtractor(track);
            if (audioFrameExtractor instanceof AudioFrameExtractor) {
                await AudioFrameExtractor.initialize();
            }
        }
        catch (error) {
            this.pendingTrackIds.delete(trackId);
            throw error;
        }
        const rawOutputTrack = audioFrameExtractor.getOutputTrack();
        const gainAppliedTrack = new GainApplyingTrack(rawOutputTrack, () => this.inputTrackGains.get(trackId) ?? 1);
        this.audioFrameExtractors.set(trackId, audioFrameExtractor);
        this.pendingTrackIds.delete(trackId);
        this.inputAudioTracks.set(trackId, track);
        if (this.compositeAudioExtractor && this.audioPlayer) {
            this.audioPlayer.addInputSourceToCapture(track);
        }
        audioFrameExtractor.start();
        if (this.perTrackEncodingEnabled && this.audioEncoderWorkerProxy) {
            const workletNode = audioFrameExtractor instanceof AudioFrameExtractor
                ? audioFrameExtractor.getAudioWorkletNode()
                : undefined;
            if (workletNode) {
                const ch = new MessageChannel();
                const timestampOffsetMicros = Timekeeper.getInstance('producer').getLastTimestampMicros();
                workletNode.port.postMessage({ type: 'setEncoderPort', port: ch.port1 }, [ch.port1]);
                await this.audioEncoderWorkerProxy.connectWorkletPort(transfer(ch.port2, [ch.port2]), trackId, timestampOffsetMicros);
            }
            await this.audioEncoderWorkerProxy.addPerTrackEncoder(proxy(gainAppliedTrack), trackId);
        }
    }
    async removeMediaStreamAudioTrack(track) {
        const trackId = track.id;
        const audioFrameExtractor = this.audioFrameExtractors.get(trackId);
        if (audioFrameExtractor) {
            await audioFrameExtractor.stop();
            this.audioFrameExtractors.delete(trackId);
        }
        if (this.audioEncoderWorkerProxy) {
            await this.audioEncoderWorkerProxy.disconnectWorkletPort(trackId);
            await this.audioEncoderWorkerProxy.removePerTrackEncoder(trackId);
        }
        this.inputAudioTracks.delete(trackId);
        this.inputTrackGains.delete(trackId);
        this.audioPlayer?.removeInputSourceFromCapture(trackId);
    }
    // ========== Composite audio (extractor on main thread, encoder in worker) ==========
    /**
     * Start capturing mixed audio. Creates the composite extractor but does
     * NOT create encoders. Use startCompositeEncoder / stopCompositeEncoder
     * to manage individual encoders.
     */
    async startMixedAudioCapture() {
        if (this.compositeAudioExtractor) {
            console.warn(`${this.LOG_PREFIX} Mixed audio capture already started`);
            return;
        }
        const audioMediaTrack = this.audioPlayer?.startAudioCapture();
        if (!audioMediaTrack) {
            throw new Error('AudioPlayer not initialized');
        }
        for (const [, track] of this.inputAudioTracks) {
            this.audioPlayer?.addInputSourceToCapture(track);
        }
        this.compositeAudioExtractor =
            createAudioFrameExtractor(audioMediaTrack);
        if (this.compositeAudioExtractor instanceof AudioFrameExtractor) {
            await AudioFrameExtractor.initialize();
        }
        this.compositeAudioExtractor.start();
        console.log(`${this.LOG_PREFIX} mixed audio capture started`);
    }
    /**
     * Add a composite encoder for a given role. The encoder runs in the
     * AudioEncoderWorker and encodes the mixed audio capture output.
     */
    async startCompositeEncoder(role, codec, bitrate, channels, callback) {
        if (!this.audioEncoderWorkerProxy) {
            throw new Error('audioEncoderWorker must be running');
        }
        if (!this.compositeAudioExtractor) {
            throw new Error('Mixed audio capture must be started first (call startMixedAudioCapture)');
        }
        console.log(`${this.LOG_PREFIX} startCompositeEncoder`, {
            role,
            codec,
            hasExtractor: !!this.compositeAudioExtractor,
        });
        const extractorOutputTrack = this.compositeAudioExtractor.getOutputTrack();
        await this.audioEncoderWorkerProxy.addCompositeEncoder(role, codec, bitrate, channels, proxy(extractorOutputTrack), proxy(callback));
    }
    /**
     * Remove a composite encoder by role.
     */
    async stopCompositeEncoder(role) {
        await this.audioEncoderWorkerProxy?.removeCompositeEncoder(role);
    }
    /**
     * Stop capturing the mixed audio output and clean up.
     */
    async stopMixedAudioCapture() {
        const hadExtractor = !!this.compositeAudioExtractor;
        this.audioPlayer?.stopAudioCapture();
        if (this.compositeAudioExtractor) {
            this.compositeAudioExtractor.stop();
            this.compositeAudioExtractor = undefined;
        }
        console.log(`${this.LOG_PREFIX} mixed audio capture stopped`, {
            hadExtractor,
        });
    }
    // ========== N-1 audio (per-guest mix excluding their own track) ==========
    n1Extractors = new Map();
    /**
     * Start an N-1 encoder that mixes all audio except the given track
     * and delivers encoded frames to the callback.
     */
    async startN1Encoder(excludedTrackId, codec, bitrate, channels, callback) {
        if (!this.audioEncoderWorkerProxy) {
            throw new Error('audioEncoderWorker must be running');
        }
        if (!this.audioPlayer) {
            throw new Error('AudioPlayer not initialized');
        }
        if (this.n1Extractors.has(excludedTrackId)) {
            console.warn(`${this.LOG_PREFIX} N-1 encoder already running for ${excludedTrackId}`);
            return;
        }
        const captureTrack = this.audioPlayer.createN1Capture(excludedTrackId);
        const extractor = createAudioFrameExtractor(captureTrack);
        if (extractor instanceof AudioFrameExtractor) {
            await AudioFrameExtractor.initialize();
        }
        extractor.start();
        this.n1Extractors.set(excludedTrackId, extractor);
        const role = `n1:${excludedTrackId}`;
        const outputTrack = extractor.getOutputTrack();
        await this.audioEncoderWorkerProxy.addCompositeEncoder(role, codec, bitrate, channels, proxy(outputTrack), proxy(callback));
        console.log(`${this.LOG_PREFIX} N-1 encoder started, excluding ${excludedTrackId}`);
    }
    async stopN1Encoder(excludedTrackId) {
        const role = `n1:${excludedTrackId}`;
        await this.audioEncoderWorkerProxy?.removeCompositeEncoder(role);
        const extractor = this.n1Extractors.get(excludedTrackId);
        if (extractor) {
            extractor.stop();
            this.n1Extractors.delete(excludedTrackId);
        }
        this.audioPlayer?.removeN1Capture(excludedTrackId);
        console.log(`${this.LOG_PREFIX} N-1 encoder stopped for ${excludedTrackId}`);
    }
    async stopAllN1Encoders() {
        for (const excludedTrackId of [...this.n1Extractors.keys()]) {
            await this.stopN1Encoder(excludedTrackId);
        }
    }
    // ========== Content audio stop/resume (guest-side) ==========
    stopContentAudioTracks() {
        const activeBeforeStop = this.audioPlayer?.getActiveTrackIds() ?? [];
        const removed = this.audioPlayer?.stopContentAudioTracks() ?? [];
        logGapDiagnostic('audioEngine.stopContentAudioTracks', {
            detail: {
                removedTrackIds: removed,
                activeBeforeStop,
                activeAfterStop: this.audioPlayer?.getActiveTrackIds() ?? [],
            },
        });
        return removed;
    }
    resumeContentAudioTracks() {
        logGapDiagnostic('audioEngine.resumeContentAudioTracks', {
            detail: {
                activeTracks: this.audioPlayer?.getActiveTrackIds() ?? [],
            },
        });
        this.audioPlayer?.resumeContentAudioTracks();
    }
    exemptTrackFromContentStop(trackId) {
        this.audioPlayer?.exemptTrackFromContentStop(trackId);
    }
    async destroyDecoderTrack(trackId) {
        logGapDiagnostic('audioEngine.destroyDecoderTrack', {
            trackId,
            detail: {
                activePlayerTracks: this.audioPlayer?.getActiveTrackIds() ?? [],
            },
        });
        await this.audioDecoderWorkerProxy?.destroyTrack(trackId);
        this.audioPlayer?.removeTrack(trackId);
    }
    // ========== Audio decoding & playback ==========
    async feedEncodedAudioFrame(frame) {
        let decoder = this.audioDecoders.get(frame.trackId);
        if (!decoder) {
            decoder = await createAudioDecoder(frame.trackId, frame.codec ?? Settings.audioCodec);
            await decoder.init((error) => {
                console.error(`${this.LOG_PREFIX} decoder error for track ${frame.trackId}:`, error);
            });
            decoder
                .getUnencodedOutputTrack()
                .setListener(async (unencodedAudioFrame, done) => {
                if (done) {
                    this.audioDecoders.delete(frame.trackId);
                    return;
                }
                if (unencodedAudioFrame) {
                    this.audioPlayer?.feedAudioPacket({
                        trackId: unencodedAudioFrame.trackId,
                        audioData: {
                            channels: [unencodedAudioFrame.data],
                            timestamp: unencodedAudioFrame.timestamp,
                            duration: unencodedAudioFrame.duration,
                        },
                        endOfTrack: unencodedAudioFrame.done,
                    });
                }
            });
            this.audioDecoders.set(frame.trackId, decoder);
        }
        if (!this.incomingTracks.has(frame.trackId)) {
            this.incomingTracks.set(frame.trackId, new IncomingTrack(frame.trackId, frame.userSessionId, async (f) => {
                if (f) {
                    decoder?.feedEncodedAudioFrame(f);
                }
            }));
        }
        this.incomingTracks.get(frame.trackId)?.feedFrame(frame, frame.done);
    }
    // ========== Playback controls ==========
    async resumeAllContexts() {
        this.audioPlayer?.flushAllTracks();
        await this.audioContext.resume();
        await AudioFrameExtractor.resumeContext();
    }
    async suspendAllContexts() {
        await this.audioContext.suspend();
        await AudioFrameExtractor.suspendContext();
    }
    setTrackMuted(trackId, muted) {
        if (this.inputAudioTracks.has(trackId)) {
            const g = muted ? 0 : 1;
            this.inputTrackGains.set(trackId, g);
            this.audioPlayer?.setInputSourceCaptureGain(trackId, g);
            return;
        }
        this.audioPlayer?.setTrackGain(trackId, muted ? 0 : 1);
    }
    setTrackGain(trackId, gain) {
        if (this.inputAudioTracks.has(trackId)) {
            this.inputTrackGains.set(trackId, gain);
            this.audioPlayer?.setInputSourceCaptureGain(trackId, gain);
            return;
        }
        this.audioPlayer?.setTrackGain(trackId, gain);
    }
    getTrackGain(trackId) {
        if (this.inputAudioTracks.has(trackId)) {
            return this.inputTrackGains.get(trackId) ?? 1;
        }
        return this.audioPlayer?.getTrackGain(trackId) ?? 1;
    }
    async handleLeave(userSessionId) {
        const trackIdsToDelete = [];
        for (const [trackId, track] of this.incomingTracks) {
            if (track.getUserSessionId() === userSessionId) {
                trackIdsToDelete.push(trackId);
            }
        }
        console.log(`${this.LOG_PREFIX} handleLeave`, {
            userSessionId,
            trackIdsToDelete,
        });
        logGapDiagnostic('audioEngine.handleLeave', {
            detail: {
                userSessionId,
                trackIdsToDelete,
                activePlayerTracks: this.audioPlayer?.getActiveTrackIds() ?? [],
            },
        });
        for (const trackId of trackIdsToDelete) {
            this.incomingTracks.delete(trackId);
            const decoder = this.audioDecoders.get(trackId);
            if (decoder) {
                await decoder.destroy();
                this.audioDecoders.delete(trackId);
            }
            this.audioPlayer?.removeTrack(trackId);
        }
    }
    // ========== Worker creation ==========
    createAudioEncoderWorker() {
        if (import.meta.env.BUILD_TRANSFORMS_FILENAMES) {
            return new Worker(new URL('./AudioEncoderWorker.ts', import.meta.url), { type: 'module' });
        }
        const ext = import.meta.env.DEV ? '.ts' : '.js';
        return createWorker(`./AudioEncoderWorker${ext}`, import.meta.url);
    }
    createAudioDecoderWorker() {
        if (import.meta.env.BUILD_TRANSFORMS_FILENAMES) {
            return new Worker(new URL('./AudioDecoderWorker.ts', import.meta.url), { type: 'module' });
        }
        const ext = import.meta.env.DEV ? '.ts' : '.js';
        return createWorker(`./AudioDecoderWorker${ext}`, import.meta.url);
    }
}
